import debounce from "lodash.debounce";
import { useMemo } from "react";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker.react";
import { useDtfStore } from "../store";
import { TabButton, Tabs } from "./components/Tabs/index";
import Custom from "./Custom/Custom.vue";
import DesignServices from "./DesignServices/DesignServices.vue";
import PrintAsIs from "./PrintAsIs/PrintAsIs.vue";
import PrintPerfect from "./PrintPerfect/PrintPerfect.vue";

export default function ArtworkQualityConfigPanel({
  index,
  artwork,
  reorderEnabled,
  paidExpertEditAddonEnabled,
  activeConfigSection,
  isMultiUpload,
  onSetActiveConfigSection
}) {
  const store = useDtfStore();

  const debouncedToggleEnhancement = useMemo(
    () =>
      debounce(async section => {
        if (!artwork?.id) {
          return;
        }
        const enableEnhancement = section !== "print_original";
        await store.updateEnableEnhancement({
          artworkId: artwork.id,
          enableEnhancement,
          enhancementTier: section
        });
        store.updateArtworkValuesById({
          id: artwork.id,
          hasExpertEditAddon: artwork.hasExpertEditAddon
        });
      }, 100),
    [artwork, store]
  );

  function executeTabChange(section) {
    if (artwork.enhancementCompleted) {
      debouncedToggleEnhancement(section);
    } else {
      store.updateArtworkValuesById({
        id: artwork.id,
        enhancementTier: section,
        pricing: store.cachedArtworkPrices[section] || {},
        hasExpertEditAddon: artwork.hasExpertEditAddon
      });
    }
    trackRudderstackImageEdited({
      product_line: store.sticker ? "sticker" : store.isProColor ? "pro color" : "dtf",
      artwork: {
        ...artwork,
        enhancementTier: section
      },
      edit_type: section === "print_original" ? "Print as uploaded" : "Jiffy Print Perfect",
      artworks_count: store.artworks.length,
      location: "pdp",
      pre_cut_transfers: store.preCut
    });
  }

  function setActiveConfigSection(section) {
    if (artwork.pinningSessionId) {
      store.setPendingPinningInvalidation({
        type: "enhancementTier",
        artworkId: artwork.id,
        value: section
      });
      return;
    }
    executeTabChange(section);
  }

  function setPrintPerfectSection() {
    setActiveConfigSection("full_enhancements");
  }

  return (
    <>
      <Tabs
        className="artwork-quality-config-panel"
        slots={{
          buttons: () => (
            <>
              <TabButton
                active={artwork.enhancementTier === "print_original"}
                icon={undefined}
                onClick={() => {
                  setActiveConfigSection("print_original");
                }}
                data-testid="tab-button"
              >
                {"Print As Is"}
              </TabButton>
              <TabButton
                active={artwork.enhancementTier === "full_enhancements"}
                icon={"stars-corners"}
                onClick={() => {
                  setActiveConfigSection("full_enhancements");
                }}
                data-testid="tab-button-1"
              >
                {"PrintPerfect"}
              </TabButton>
              {false ? (
                <TabButton
                  active={artwork.enhancementTier === "custom"}
                  icon={undefined}
                  onClick={() => {
                    setActiveConfigSection("custom");
                  }}
                  data-testid="tab-button-2"
                >
                  {"Expert Edit"}
                </TabButton>
              ) : null}
            </>
          )
        }}
        data-testid="artwork-quality-config-panel"
      >
        {artwork.enhancementTier === "print_original" ? (
          <PrintAsIs
            key={artwork.enhancementTier}
            artwork={artwork}
            onPrintPerfectClick={setPrintPerfectSection}
            data-testid="print-as-is"
          />
        ) : null}
        {artwork.enhancementTier === "full_enhancements" ? (
          <PrintPerfect
            key={artwork.enhancementTier}
            artwork={artwork}
            reorderEnabled={reorderEnabled}
            paidExpertEditAddonEnabled={paidExpertEditAddonEnabled}
            index={index}
            data-testid="print-perfect"
          />
        ) : null}
        {!paidExpertEditAddonEnabled && artwork.enhancementTier === "custom" ? (
          <Custom
            key={artwork.enhancementTier}
            artwork={artwork}
            index={index}
            reorderEnabled={reorderEnabled}
            data-testid="custom"
          />
        ) : null}
      </Tabs>
      {artwork.enhancementTier === "full_enhancements" ||
      artwork.enhancementTier === "print_original" ? (
        <DesignServices
          artwork={artwork}
          paidExpertEditAddonEnabled={Boolean(paidExpertEditAddonEnabled)}
          index={index}
          isMultiUpload={Boolean(isMultiUpload)}
          data-testid="design-services"
        />
      ) : null}
    </>
  );
}
